import type { KeyboardEvent, ReactNode } from 'react';
import type { WsAsama, WsGorulme, WsIs, WsKutu, WsOlay, WsOverview, WsTur } from './api';
import { WS } from './metin/web-sitesi';
import { bir, gunAyBae, tarihSaatBae } from './web-sitesi-bicim';

/**
 * Web sitesi bölümünün grafikleri (taslak A, 07.10.2026): kütüphanesiz, elle yazılmış SVG.
 *
 * Taslağın `lib2.js` dosyasındaki bileşenlerin React karşılığı. Çizim matematiği taslakla aynı;
 * değişen tek şey veri: sayılar canlı kayıttan geliyor. Her tıklanabilir işaret hem fare hem
 * klavyeyle çalışıyor (`Enter` ve `Space`).
 */

const NOKTA_ESIK_MAX = 40;
const LANES: readonly WsTur[] = ['destek', 'on', 'vize', 'sip'];

function tus(islem: () => void) {
  return (olay: KeyboardEvent) => {
    if (olay.key === 'Enter' || olay.key === ' ') {
      olay.preventDefault();
      islem();
    }
  };
}

function Uyari({ x = 0, y = 0, boyut = 11 }: { x?: number; y?: number; boyut?: number }) {
  return (
    <svg x={x} y={y} width={boyut} height={boyut} viewBox="0 0 12 12" aria-hidden="true">
      <path d="M6 1.2 11.2 10.4H.8Z" fill="currentColor" />
      <rect x="5.4" y="4.4" width="1.2" height="3.2" fill="var(--surface)" />
      <rect x="5.4" y="8.2" width="1.2" height="1.2" fill="var(--surface)" />
    </svg>
  );
}

export function UyariSimgesi() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <path d="M6 1.2 11.2 10.4H.8Z" fill="currentColor" />
      <rect x="5.4" y="4.4" width="1.2" height="3.2" fill="var(--surface)" />
      <rect x="5.4" y="8.2" width="1.2" height="1.2" fill="var(--surface)" />
    </svg>
  );
}

export function BilgiSimgesi() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <circle cx="6" cy="6" r="5.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <rect x="5.4" y="5" width="1.2" height="3.6" fill="currentColor" />
      <rect x="5.4" y="3" width="1.2" height="1.2" fill="currentColor" />
    </svg>
  );
}

/** Sağ üst köşesi yuvarlak, sola düz çubuk: taslağın çubuk biçimi. */
function cubuk(x: number, y: number, w: number, h: number, r: number): string {
  return `M${x},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h - r}Q${x + w},${y + h} ${x + w - r},${y + h}H${x}Z`;
}

// ---------------------------------------------------------------------------
// Sparkline: gün gün sütun, sıfır günler de çizilir
// ---------------------------------------------------------------------------

export function Spark({ kutu, aria, bugun }: { kutu: WsKutu; aria: string; bugun: Date }) {
  const vals = kutu.daily;
  const n = vals.length;
  const w = 168;
  const h = 40;
  const gap = 3;
  const bw = (w - gap * (n - 1)) / n;
  const base = h - 1;
  const max = Math.max(1, ...vals);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} role="img" aria-label={aria}>
      {vals.map((v, i) => {
        const x = i * (bw + gap);
        const recent = i >= n - 7;
        const gun = new Date(bugun.getTime() - (n - 1 - i) * 86_400_000);
        const bh = v === 0 ? 2 : Math.max(5, (v / max) * (h - 8));
        const r = Math.min(3, bw / 2, bh);
        return (
          <g key={i}>
            {v === 0 ? (
              <rect x={x} y={base - 2} width={bw} height={2} fill="var(--mute-soft)" />
            ) : (
              <path d={`M${x},${base}V${base - bh + r}Q${x},${base - bh} ${x + r},${base - bh}H${x + bw - r}Q${x + bw},${base - bh} ${x + bw},${base - bh + r}V${base}Z`} fill={recent ? 'var(--accent)' : 'var(--mute-soft)'} />
            )}
            <rect className="hit" x={x - gap / 2} y={0} width={bw + gap} height={h} fill="transparent" data-tip={`${gunAyBae(gun)}: ${v}`} />
          </g>
        );
      })}
    </svg>
  );
}

/** Dönen üye karosunun küçük kareleri: dolu son 30 günde görülen, boş çerçeve hiç oturumu olmayan. */
export function DonenKareleri({ donen }: { donen: WsOverview['boxes']['donen'] }) {
  return (
    <svg viewBox="0 0 168 40" width={168} role="img" aria-label={WS.kutu.kareAria(donen.total, donen.seen30, donen.never)}>
      {donen.squares.map((m, i) => {
        const dolu = m.daysAgo === null ? 'var(--surface)' : m.daysAgo <= 30 ? 'var(--accent)' : 'var(--mute-soft)';
        return <rect key={m.n} x={i * 18.5 + 1} y={14} width={15} height={15} rx={4} fill={dolu} stroke={m.daysAgo === null ? 'var(--danger)' : 'none'} strokeWidth={1.5} data-tip={WS.kutu.kareIpucu(m.name, m.daysAgo)} />;
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Bekleme yaşı nokta şeridi
// ---------------------------------------------------------------------------

export function NoktaSeridi({ genislik, isler, esikler, secili, ac }: { genislik: number; isler: WsIs[]; esikler: Record<WsTur, number>; secili?: string | null; ac: (i: WsIs) => void }) {
  const mob = genislik < 520;
  const L = mob ? 66 : 96;
  const R = 12;
  const T = 8;
  const B = 24;
  const LH = mob ? 54 : 58;
  const pw = genislik - L - R;
  const H = T + LANES.length * LH + B;
  const X = (d: number) => L + (Math.min(d, NOKTA_ESIK_MAX) / NOKTA_ESIK_MAX) * pw;
  const asan = isler.filter((i) => i.overdue).length;
  const ticks = mob ? [0, 7, 14, 28] : [0, 7, 14, 21, 28, 35];
  return (
    <svg viewBox={`0 0 ${genislik} ${H}`} width={genislik} height={H} role="img" aria-label={WS.yas.aria(asan)}>
      {LANES.map((ln, li) => {
        const y0 = T + li * LH;
        const th = esikler[ln];
        return (
          <g key={`bant-${ln}`}>
            <rect x={X(th)} y={y0} width={X(NOKTA_ESIK_MAX) - X(th)} height={LH} fill="var(--danger-soft)" />
            <line x1={X(th)} x2={X(th)} y1={y0} y2={y0 + LH} stroke="var(--danger)" strokeWidth={1} opacity={0.55} />
            <g color="var(--danger)" transform={`translate(${Math.min(X(th) + 6, genislik - R - (mob ? 56 : 84))},${y0 + 3})`}>
              <Uyari x={0} y={1} />
              <text x={14} y={10} fontSize={10.5} fontWeight={700} fill="var(--danger)">{WS.yas.ustu(th)}</text>
            </g>
          </g>
        );
      })}
      {ticks.map((t) => (
        <g key={`tik-${t}`}>
          <line x1={X(t)} x2={X(t)} y1={T} y2={T + LANES.length * LH} stroke="var(--line)" strokeWidth={1} />
          <text x={X(t)} y={H - 7} fontSize={11} fill="var(--ink-soft)" textAnchor={t === 0 ? 'start' : 'middle'}>{t === 0 ? '0' : WS.yas.gunKisa(t)}</text>
        </g>
      ))}
      {LANES.map((ln, li) => {
        const y0 = T + li * LH;
        const cy = y0 + LH / 2 + 4;
        const its = isler.filter((i) => i.tur === ln).sort((a, b) => a.ageDays - b.ageDays);
        const yerlesen: { x: number; dy: number }[] = [];
        const rr = 5;
        const step = 12;
        return (
          <g key={`serit-${ln}`}>
            {li > 0 ? <line x1={0} x2={genislik - R} y1={y0} y2={y0} stroke="var(--line)" /> : null}
            <text x={0} y={cy - 2} fontSize={13} fontWeight={700} fill="var(--ink)">{WS.tur[ln]}</text>
            <text x={0} y={cy + 13} fontSize={11} fill="var(--ink-soft)">{WS.yas.is(its.length)}</text>
            {its.map((it) => {
              const x = X(it.ageDays);
              let dy = 0;
              for (const off of [0, -step, step]) {
                if (!yerlesen.some((p) => Math.abs(p.x - x) < rr * 2 + 3 && Math.abs(p.dy - off) < rr * 2 + 1)) {
                  dy = off;
                  break;
                }
              }
              yerlesen.push({ x, dy });
              const secildi = secili === it.key;
              return (
                <g
                  key={it.key}
                  className="hit"
                  tabIndex={0}
                  role="button"
                  aria-label={WS.yas.noktaAria(it.shortReference, bir(it.ageDays))}
                  data-tip={WS.yas.ipucu(it.shortReference, bir(it.ageDays), it.threshold, it.overdue, it.owner === 'sende')}
                  onClick={() => ac(it)}
                  onKeyDown={tus(() => ac(it))}
                >
                  <circle cx={x} cy={cy + dy} r={13} fill="transparent" />
                  {secildi ? <circle cx={x} cy={cy + dy} r={rr + 5} fill="none" stroke="var(--ink)" strokeWidth={1.5} /> : null}
                  <circle cx={x} cy={cy + dy} r={rr} fill={it.overdue ? 'var(--danger)' : 'var(--accent)'} stroke="var(--surface)" strokeWidth={2} />
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}

export function NoktaTablosu({ isler }: { isler: WsIs[] }) {
  return (
    <table className="viz-table">
      <thead>
        <tr>
          <th>{WS.yas.tabloIs}</th>
          <th>{WS.yas.tabloTur}</th>
          <th>{WS.yas.tabloBekleme}</th>
          <th>{WS.yas.tabloKimde}</th>
        </tr>
      </thead>
      <tbody>
        {[...isler].sort((a, b) => b.ageDays - a.ageDays).map((i) => (
          <tr key={i.key}>
            <td>{i.shortReference}</td>
            <td>{WS.tur[i.tur]}</td>
            <td>{`${WS.yas.gunMetni(bir(i.ageDays))}${i.overdue ? WS.yas.esikAsildi(i.threshold) : ''}`}</td>
            <td>{i.owner === 'sende' ? WS.sahip.sende : WS.sahip.musteri}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Aşama çubukları
// ---------------------------------------------------------------------------

function AsamaSvg({ st, genislik, ac }: { st: WsAsama; genislik: number; ac: (id: WsTur, key: string) => void }) {
  const rows = st.rows;
  const lw = genislik < 300 ? 92 : 112;
  const vw = 34;
  const pw = genislik - lw - vw;
  const rh = 26;
  const H = rows.length * rh + 4;
  const MAX = Math.max(1, ...rows.map((r) => r.count));
  return (
    <svg viewBox={`0 0 ${genislik} ${H}`} width={genislik} height={H} role="img" aria-label={WS.asama.aria(st.title, rows.map((r) => `${r.label} ${r.count}`).join(', '))}>
      <line x1={lw} x2={lw} y1={0} y2={H} stroke="var(--line-strong)" />
      {rows.map((r, i) => {
        const y = i * rh + 2;
        const bw = r.count === 0 ? 0 : Math.max(6, (r.count / MAX) * pw);
        const hl = r.highlight;
        const h = 14;
        const yy = y + (rh - h) / 2;
        return (
          <g key={r.key} className="hit" tabIndex={0} role="button" data-tip={WS.asama.ipucu(st.title, r.label, r.count)} onClick={() => ac(st.id, r.key)} onKeyDown={tus(() => ac(st.id, r.key))}>
            <rect x={0} y={y} width={genislik} height={rh} fill="transparent" />
            <text x={0} y={y + 17} fontSize={12.5} fill="var(--ink)" fontWeight={hl ? 700 : 400}>{r.label}</text>
            {bw > 0 ? <path d={cubuk(lw, yy, bw, h, Math.min(4, bw))} fill={hl ? 'var(--accent)' : 'var(--mute)'} /> : null}
            <text x={lw + bw + 7} y={y + 17} fontSize={12.5} fontWeight={700} fill="var(--ink)">{r.count}</text>
          </g>
        );
      })}
    </svg>
  );
}

export function AsamaBloklari({ genislik, asamalar, kolon, ac, yalniz }: { genislik: number; asamalar: WsAsama[]; kolon: number; ac: (id: WsTur, key: string) => void; yalniz?: WsTur }) {
  const liste = asamalar.filter((a) => !yalniz || a.id === yalniz);
  const w = kolon === 1 ? genislik : Math.floor((genislik - 32) / 2);
  return (
    <div className="stage-grid" style={{ gridTemplateColumns: `repeat(${kolon},minmax(0,1fr))` }}>
      {liste.map((st) => (
        <div className="stage" key={st.id}>
          <h4>
            {st.title}
            <small>{st.sub}</small>
          </h4>
          <AsamaSvg st={st} genislik={w} ac={ac} />
          <p className={st.warn ? 'note' : 'note ok'}>
            {st.warn ? <UyariSimgesi /> : null}
            {st.note}
          </p>
          {st.crm ? (
            <p className="note ok">
              <BilgiSimgesi />
              {st.crm}
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function AsamaTablosu({ asamalar }: { asamalar: WsAsama[] }) {
  return (
    <table className="viz-table">
      <thead>
        <tr>
          <th>{WS.asama.tabloKaynak}</th>
          <th>{WS.asama.tabloAsama}</th>
          <th>{WS.asama.tabloIs}</th>
        </tr>
      </thead>
      <tbody>
        {asamalar.flatMap((st) => st.rows.map((r) => (
          <tr key={`${st.id}-${r.key}`}>
            <td>{st.title}</td>
            <td>{r.label}</td>
            <td>{r.count}</td>
          </tr>
        )))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Dönüşüm
// ---------------------------------------------------------------------------

const DONUSUM_RENK = ['var(--o1)', 'var(--o2)', 'var(--o3)', 'var(--o4)'];

export function DonusumSvg({ genislik, adimlar, ac }: { genislik: number; adimlar: WsOverview['funnel']; ac: (i: number) => void }) {
  const sm = genislik < 420;
  const lw = sm ? 96 : 190;
  const vw = 30;
  const pw = genislik - lw - vw;
  const rh = 38;
  const H = adimlar.length * rh + 4;
  const MAX = Math.max(1, ...adimlar.map((f) => f.count));
  return (
    <svg viewBox={`0 0 ${genislik} ${H}`} width={genislik} height={H} role="img" aria-label={WS.donusum.aria(adimlar.map((f) => `${f.label} ${f.count}`).join(', '))}>
      <line x1={lw} x2={lw} y1={0} y2={H} stroke="var(--line-strong)" />
      {adimlar.map((f, i) => {
        const y = i * rh + 2;
        const bw = f.count === 0 ? 0 : (f.count / MAX) * pw;
        const h = 20;
        const yy = y + (rh - h) / 2;
        return (
          <g key={f.label} className="hit" tabIndex={0} role="button" data-tip={WS.donusum.ipucu(f.label, f.count)} onClick={() => ac(i)} onKeyDown={tus(() => ac(i))}>
            <rect x={0} y={y} width={genislik} height={rh} fill="transparent" />
            <text x={0} y={y + 23} fontSize={12.5} fill="var(--ink)">{sm ? f.short : f.label}</text>
            {bw > 0 ? <path d={cubuk(lw, yy, bw, h, 4)} fill={DONUSUM_RENK[i] ?? 'var(--o4)'} /> : null}
            <text x={lw + bw + 7} y={y + 23} fontSize={13} fontWeight={700} fill="var(--ink)">{f.count}</text>
          </g>
        );
      })}
    </svg>
  );
}

export function DonusumTablosu({ adimlar }: { adimlar: WsOverview['funnel'] }) {
  return (
    <table className="viz-table">
      <thead>
        <tr>
          <th>{WS.donusum.tabloAsama}</th>
          <th>{WS.donusum.tabloKisi}</th>
        </tr>
      </thead>
      <tbody>
        {adimlar.map((f) => (
          <tr key={f.label}>
            <td>{f.label}</td>
            <td>{f.count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Kayıp riski
// ---------------------------------------------------------------------------

function kisaAd(ad: string, en = 12): string {
  const t = ad.trim();
  return t.length <= en ? t : `${t.slice(0, en - 1)}…`;
}

export function RiskSvg({ genislik, risk, ac }: { genislik: number; risk: WsOverview['risk']; ac: (externalId: string) => void }) {
  const seen = risk.seen;
  const never = risk.never;
  const mob = genislik < 520;
  const L = mob ? 74 : 96;
  const R = 14;
  const T = 28;
  const RH = 26;
  const pw = genislik - L - R;
  const XM = 40;
  const X = (d: number) => L + (Math.min(d, XM) / XM) * pw;
  const yN = T + seen.length * RH + 40;
  const H = yN + never.length * RH + 30;
  return (
    <svg viewBox={`0 0 ${genislik} ${H}`} width={genislik} height={H} role="img" aria-label={WS.risk.aria(risk.total, risk.seenTotal, risk.neverTotal)}>
      <rect x={X(30)} y={T - 6} width={X(XM) - X(30)} height={seen.length * RH + 6} fill="var(--danger-soft)" />
      {[0, 7, 14, 21, 30, 40].filter((t) => !(mob && (t === 14 || t === 21))).map((t) => {
        if (t === 30) return <line key={t} x1={X(30)} x2={X(30)} y1={T - 6} y2={T + seen.length * RH} stroke="var(--line-strong)" />;
        const key = t === 7;
        return (
          <g key={t}>
            <line x1={X(t)} x2={X(t)} y1={T - 6} y2={T + seen.length * RH} stroke={key ? 'var(--line-strong)' : 'var(--line)'} strokeWidth={1} />
            <text x={X(t)} y={key ? T - 12 : T + seen.length * RH + 16} fontSize={11} fill="var(--ink-soft)" textAnchor={t === 0 ? 'start' : 'middle'}>{key ? WS.risk.gun7 : t === 0 ? '0' : t}</text>
          </g>
        );
      })}
      <g color="var(--danger)" transform={`translate(${X(30) + 6},${T - 20})`}>
        <Uyari x={0} y={2} boyut={12} />
        <text x={17} y={11} fontSize={11} fontWeight={700} fill="var(--danger)">{mob ? WS.risk.otuzKisa : WS.risk.otuzGecti}</text>
      </g>
      {seen.map((m: WsGorulme, i) => {
        const cy = T + i * RH + RH / 2;
        const x = X(m.daysAgo ?? 0);
        const ver = m.emailVerified;
        return (
          <g key={m.externalId} className="hit" tabIndex={0} role="button" aria-label={`${m.name}, ${WS.gunOnce(Math.round(m.daysAgo ?? 0))}`} data-tip={WS.risk.ipucuGorulen(m.name, Math.round(m.daysAgo ?? 0), ver)} onClick={() => ac(m.externalId)} onKeyDown={tus(() => ac(m.externalId))}>
            <rect x={0} y={cy - RH / 2} width={genislik} height={RH} fill="transparent" />
            <text x={0} y={cy + 4} fontSize={12.5} fill="var(--ink)">{kisaAd(m.name, mob ? 9 : 13)}</text>
            <line x1={X(0)} x2={x} y1={cy} y2={cy} stroke="var(--line-strong)" strokeWidth={1} />
            <circle cx={x} cy={cy} r={5} fill={ver ? 'var(--accent)' : 'var(--surface)'} stroke={ver ? 'var(--surface)' : 'var(--accent)'} strokeWidth={2} />
          </g>
        );
      })}
      <text x={0} y={yN - 5} fontSize={11} fontWeight={700} fill="var(--ink-soft)">{WS.risk.hicOturum(risk.neverTotal)}</text>
      {never.map((m, i) => {
        const cy = yN + i * RH + RH / 2 + 4;
        return (
          <g key={m.externalId} className="hit" tabIndex={0} role="button" aria-label={`${m.name}, ${WS.risk.hicOturumYok}`} data-tip={WS.risk.ipucuHic(m.name)} onClick={() => ac(m.externalId)} onKeyDown={tus(() => ac(m.externalId))}>
            <rect x={0} y={cy - RH / 2} width={genislik} height={RH} fill="transparent" />
            <text x={0} y={cy + 4} fontSize={12.5} fill="var(--ink)">{kisaAd(m.name, mob ? 9 : 13)}</text>
            <rect x={X(0) - 4} y={cy - 4} width={8} height={8} transform={`rotate(45 ${X(0)} ${cy})`} fill="var(--surface)" stroke="var(--danger)" strokeWidth={2} />
            <text x={X(0) + 14} y={cy + 4} fontSize={12} fill="var(--ink-soft)">{WS.risk.hicNot(Math.round(m.registeredDaysAgo))}</text>
          </g>
        );
      })}
    </svg>
  );
}

export function RiskLejandi() {
  return (
    <div className="legend">
      <span>
        <svg width="14" height="14"><circle cx="7" cy="7" r="5" fill="var(--accent)" stroke="var(--surface)" strokeWidth="2" /></svg>
        {WS.risk.dogrulanmis}
      </span>
      <span>
        <svg width="14" height="14"><circle cx="7" cy="7" r="5" fill="var(--surface)" stroke="var(--accent)" strokeWidth="2" /></svg>
        {WS.risk.dogrulanmamis}
      </span>
      <span style={{ color: 'var(--danger)' }}>
        <UyariSimgesi />
        <span style={{ color: 'var(--ink-soft)' }}>{WS.risk.otuzGecti}</span>
      </span>
    </div>
  );
}

export function RiskTablosu({ risk }: { risk: WsOverview['risk'] }) {
  return (
    <table className="viz-table">
      <thead>
        <tr>
          <th>{WS.risk.tabloUye}</th>
          <th>{WS.risk.tabloGorulme}</th>
          <th>{WS.risk.tabloEposta}</th>
          <th>{WS.risk.tabloKayit}</th>
        </tr>
      </thead>
      <tbody>
        {[...risk.seen, ...risk.never].map((m) => (
          <tr key={m.externalId}>
            <td>{m.name}</td>
            <td>{m.daysAgo === null ? WS.risk.hicOturumYok : WS.gunOnce(Math.round(m.daysAgo))}</td>
            <td>{m.emailVerified ? WS.risk.dogrulanmisKucuk : WS.risk.dogrulanmamisKucuk}</td>
            <td>{WS.gunOnce(Math.round(m.registeredDaysAgo))}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Kişi sayfası: son 40 gün çizgisi
// ---------------------------------------------------------------------------

export function KisiSeridi({ genislik, ad, olaylar, simdi }: { genislik: number; ad: string; olaylar: WsOlay[]; simdi: Date }) {
  const mob = genislik < 520;
  const L = mob ? 58 : 74;
  const R = 14;
  const pw = genislik - L - R;
  const X = (a: number) => L + (1 - Math.min(Math.max(a, 0), 40) / 40) * pw;
  const satirlar = [WS.kisi.seritUyelik, WS.kisi.seritOturum, WS.kisi.seritSiparis];
  const RH = 34;
  const T = 6;
  const H = T + satirlar.length * RH + 24;
  const yas = (at: string | null) => (at === null ? null : (simdi.getTime() - Date.parse(at)) / 86_400_000);
  const nokta = (satir: number, a: number, n: number, renk: string, ipucu: string, key: string) => {
    const cy = T + satir * RH + RH / 2;
    const x = X(a);
    const rr = n > 1 ? 9 : 5;
    return (
      <g key={key} data-tip={ipucu}>
        <circle cx={x} cy={cy} r={rr} fill={renk} stroke="var(--surface)" strokeWidth={2} />
        {n > 1 ? <text x={x} y={cy + 4} fontSize={10.5} fontWeight={700} fill="#fff" textAnchor="middle">{n}</text> : null}
      </g>
    );
  };
  const ogeler: ReactNode[] = [];
  const kume = new Map<number, WsOlay[]>();
  for (const o of olaylar) {
    const a = yas(o.at);
    if (a === null || a > 40) continue;
    if (o.type === 'uye') ogeler.push(nokta(0, a, 1, 'var(--accent)', `${o.text}: ${tarihSaatBae(o.at ?? '').slice(0, 10)}`, `u-${o.at}-${o.text}`));
    else if (o.type === 'oturum') ogeler.push(nokta(1, a, 1, 'var(--accent)', `${o.text}: ${tarihSaatBae(o.at ?? '').slice(0, 10)}`, `o-${o.at}`));
    else if (o.type === 'sip') {
      const k = Math.round(a * 3);
      kume.set(k, [...(kume.get(k) ?? []), o]);
    }
  }
  for (const [k, g] of kume) {
    const ort = g.reduce((t, o) => t + (yas(o.at) ?? 0), 0) / g.length;
    const bekleyen = g.some((o) => o.tone === 'open');
    ogeler.push(nokta(2, ort, g.length, bekleyen ? 'var(--warn)' : 'var(--mute)', WS.kisi.siparisIpucu(g.length, tarihSaatBae(g[0]?.at ?? '').slice(0, 10), bekleyen), `s-${k}`));
  }
  return (
    <svg viewBox={`0 0 ${genislik} ${H}`} width={genislik} height={H} role="img" aria-label={WS.kisi.seritAria(ad)}>
      {(mob ? [0, 20, 40] : [0, 10, 20, 30, 40]).map((t) => (
        <g key={t}>
          <line x1={X(t)} x2={X(t)} y1={T} y2={T + satirlar.length * RH} stroke="var(--line)" />
          <text x={X(t)} y={H - 6} fontSize={11} fill="var(--ink-soft)" textAnchor={t === 0 ? 'end' : t === 40 ? 'start' : 'middle'}>{t === 0 ? WS.kisi.bugun : WS.kisi.gunOnceKisa(t)}</text>
        </g>
      ))}
      {satirlar.map((r, i) => {
        const cy = T + i * RH + RH / 2;
        return (
          <g key={r}>
            {i > 0 ? <line x1={0} x2={genislik - R} y1={T + i * RH} y2={T + i * RH} stroke="var(--line)" /> : null}
            <text x={0} y={cy + 4} fontSize={12.5} fontWeight={700} fill="var(--ink)">{r}</text>
          </g>
        );
      })}
      {ogeler}
    </svg>
  );
}
